import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { markdownToHtml } from './RichTextEditor';
import { sanitizeHtml } from '../utils/sanitize';

const API_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) || '';

// Keyed on the splash content so that when admin publishes something new,
// users who ticked "Don't show again" previously still see the fresh
// announcement. Fingerprint is intentionally lossy — title + first 80 chars
// of description is enough to differentiate distinct campaigns while being
// resilient to minor copy tweaks.
const PERM_DISMISS_KEY = 'splash_permanently_dismissed_v1';
const splashFingerprint = (s) =>
  `${s?.title || ''}|${(s?.description || '').slice(0, 80)}`;

const SplashAnnouncement = () => {
  const [splash, setSplash] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [_isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAndFetchSplash();
  }, []);

  // Allow the user to dismiss with Escape — helps keyboard users and, more
  // importantly, mobile users whose scroll position may drift below the
  // close button on tall splashes.
  useEffect(() => {
    if (!isVisible) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAndFetchSplash = async () => {
    // Check if already dismissed this session
    const dismissed = sessionStorage.getItem('splash_dismissed');

    try {
      const response = await fetch(`${API_URL}/api/splash`);
      const data = await response.json();

      if (data.is_enabled && data.title) {
        // Respect a previously checked "Don't show again" — but only if the
        // content fingerprint still matches. When admin updates the splash
        // the fingerprint changes and the user sees the new one.
        try {
          const stored = localStorage.getItem(PERM_DISMISS_KEY);
          if (stored && stored === splashFingerprint(data)) {
            return;
          }
        } catch {
          // localStorage blocked (private mode / sandboxed iframe) — ignore.
        }

        // If show_once_per_session is true and already dismissed, don't show
        if (data.show_once_per_session && dismissed) {
          return;
        }

        setSplash(data);
        // Small delay for dramatic effect
        setTimeout(() => setIsVisible(true), 500);
      }
    } catch (error) {
      console.error('Error fetching splash:', error);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    if (splash?.show_once_per_session) {
      sessionStorage.setItem('splash_dismissed', 'true');
    }
    if (dontShowAgain && splash) {
      try {
        localStorage.setItem(PERM_DISMISS_KEY, splashFingerprint(splash));
      } catch {
        // ignore storage errors — user will just see the splash again.
      }
    }
    setTimeout(() => setSplash(null), 300);
  };

  const handleCtaClick = () => {
    if (splash?.cta_link) {
      if (splash.cta_link.startsWith('http')) {
        window.open(splash.cta_link, '_blank');
      } else {
        navigate(splash.cta_link);
      }
    }
    handleClose();
  };

  const nextMedia = () => {
    if (splash?.media?.length > 1) {
      setCurrentMediaIndex((prev) => (prev + 1) % splash.media.length);
    }
  };

  const prevMedia = () => {
    if (splash?.media?.length > 1) {
      setCurrentMediaIndex((prev) => (prev - 1 + splash.media.length) % splash.media.length);
    }
  };

  if (!splash) return null;

  const currentMedia = splash.media?.[currentMediaIndex];
  const hasMultipleMedia = splash.media?.length > 1;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-2 sm:p-4 transition-all duration-500 ${
        isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className={`relative w-full max-w-4xl max-h-[calc(100dvh-1rem)] sm:max-h-[90vh] overflow-auto rounded-2xl bg-gradient-to-br ${splash.background_color || 'from-blue-600 to-purple-700'} text-white shadow-2xl transform transition-all duration-500 ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        {/* Close button — kept sticky so it never scrolls out of view on
            tall mobile splashes. pt safe-area avoids the iOS notch. */}
        <div
          className="sticky top-0 z-20 flex items-center justify-between gap-2 p-2 pointer-events-none"
          style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}
        >
          {/* "Don't show again" — permanently dismisses this specific
              announcement (fingerprinted by content). Admin publishing a
              new splash resets dismissal for all users automatically. */}
          <label
            className="pointer-events-auto flex items-center gap-2 px-3 py-2 rounded-full bg-black/55 hover:bg-black/75 border border-white/30 shadow-lg backdrop-blur-sm text-white text-xs font-medium cursor-pointer select-none transition-colors"
            data-testid="splash-dont-show-again-label"
          >
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-4 h-4 accent-white cursor-pointer"
              data-testid="splash-dont-show-again"
            />
            <span>Don't show again</span>
          </label>
          <button
            onClick={handleClose}
            className="pointer-events-auto flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/55 hover:bg-black/75 border border-white/30 shadow-lg backdrop-blur-sm text-white text-xs font-semibold uppercase tracking-wide transition-colors"
            aria-label="Close"
            data-testid="splash-close-btn"
          >
            <X className="w-4 h-4" />
            <span>Close</span>
          </button>
        </div>

        <div className="px-5 pt-2 pb-8 sm:p-10 md:p-12">
          {/* Content */}
          <div className="text-center space-y-4">
            {/* Subtitle */}
            {splash.subtitle && (
              <p className="text-white/80 text-xs sm:text-sm uppercase tracking-wider font-medium animate-fade-in">
                {splash.subtitle}
              </p>
            )}

            {/* Title */}
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold leading-tight">
              {splash.title}
            </h2>
            
            {/* Description */}
            {splash.description && (
              <div 
                className="prose prose-invert prose-lg max-w-2xl mx-auto text-white/90"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(markdownToHtml(splash.description)) }}
              />
            )}

            {/* Media Section */}
            {splash.media?.length > 0 && (
              <div className="mt-8 relative">
                {currentMedia?.type === 'video' ? (
                  <div className="relative max-w-2xl mx-auto">
                    <video 
                      src={currentMedia.url} 
                      className="w-full rounded-xl shadow-lg"
                      controls
                      autoPlay
                      muted
                      loop
                      playsInline
                      onPlay={() => setIsVideoPlaying(true)}
                      onPause={() => setIsVideoPlaying(false)}
                    />
                  </div>
                ) : (
                  <div className="relative">
                    {/* Image Carousel */}
                    <div className="flex justify-center">
                      <img 
                        src={currentMedia?.url} 
                        alt="" 
                        className="max-h-80 rounded-xl shadow-lg object-contain"
                      />
                    </div>
                    
                    {/* Carousel Controls */}
                    {hasMultipleMedia && (
                      <>
                        <button
                          onClick={prevMedia}
                          className="absolute left-0 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                        >
                          <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                          onClick={nextMedia}
                          className="absolute right-0 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                        >
                          <ChevronRight className="w-6 h-6" />
                        </button>
                        
                        {/* Dots indicator */}
                        <div className="flex justify-center gap-2 mt-4">
                          {splash.media.map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentMediaIndex(index)}
                              className={`w-2 h-2 rounded-full transition-all ${
                                index === currentMediaIndex 
                                  ? 'bg-white w-6' 
                                  : 'bg-white/40 hover:bg-white/60'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* CTA Button */}
            {splash.cta_text && (
              <div className="pt-6">
                <Button 
                  size="lg" 
                  onClick={handleCtaClick}
                  className="bg-white text-slate-900 hover:bg-white/90 font-semibold px-8 py-3 text-lg shadow-lg hover:shadow-xl transition-all"
                >
                  {splash.cta_text}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-2xl" />
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-white/10 rounded-full translate-x-1/4 translate-y-1/4 blur-3xl" />
      </div>
    </div>
  );
};

export default SplashAnnouncement;
